import {
  useGetInventoryBatchLogs,
  exportInventoryBatchLogs,
} from "@/requests/gen/react-query/inventory-batch-log";
import {
  InventoryBatchLogTypeEnum,
  ProductTypeEnum,
} from "@/requests/gen/react-query/fastAPI.schemas";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  ParamDataTableToolbar,
  ParamSearchFilter,
  ParamClearButton,
  ParamDataTable,
  dataTableSearchParsers,
  ParamDateFilter,
} from "@/components/ui/param-data-table";
import { ColumnDef } from "@tanstack/react-table";
import { parseAsArrayOf, parseAsStringEnum, useQueryStates } from "nuqs";
import { DropdownFilter } from "@/components/ui/dropdown-filter";
import { keepPreviousData } from "@tanstack/react-query";
import { Header } from "@/components/universal/header";
import { Badge } from "@/components/ui/badge";
import { format } from "date-fns";
import { useState } from "react";
import { useGetEntitiesInfinite } from "@/requests/gen/react-query/entity";
import { useOnlyEntity } from "@/hooks/use-only-entity";
import {
  createFileRoute,
  linkOptions,
  type LinkProps,
} from "@tanstack/react-router";
import { ExportCSV } from "@/components/ui/csv-export";

export const Route = createFileRoute(
  "/_private/_inventory-360/inventory-batch-log/lookup",
)({
  component: InventoryBatchLogLookup,
});

interface LogTableProps {
  link?: LinkProps;
  inventory_batch_id?: string;
  product_name: string;
  log_type: InventoryBatchLogTypeEnum;
  quantity: number;
  created_at: string;
  suborder_id?: string;
  patient_name: string | null;
  doctor_name: string | null;
  doctor_npi: string | null;
  rx_number: string | null;
  prescribed_at: string | null;
  note: string | null;
}

const columns: ColumnDef<LogTableProps>[] = [
  {
    header: "Batch ID",
    accessorKey: "inventory_batch_id",
    cell: ({ row }) => row.original.inventory_batch_id ?? "--",
  },
  {
    header: "Product Name",
    accessorKey: "product_name",
  },
  {
    header: "Type",
    accessorKey: "log_type",
    cell: ({ row }) => {
      const type = row.original.log_type;
      const variant =
        type === InventoryBatchLogTypeEnum.Received
          ? "success"
          : type === InventoryBatchLogTypeEnum.Dispensed
            ? "default"
            : type === InventoryBatchLogTypeEnum.Adjustment
              ? "warning"
              : "destructive";
      return <Badge variant={variant}>{type}</Badge>;
    },
  },
  {
    header: "Quantity",
    accessorKey: "quantity",
    cell: ({ row }) => {
      const qty = row.original.quantity;
      return (
        <span className={qty > 0 ? "text-success" : "text-destructive"}>
          {qty > 0 ? `+${qty}` : qty}
        </span>
      );
    },
  },
  {
    header: "Date",
    accessorKey: "created_at",
    cell: ({ row }) => format(row.original.created_at, "MM-dd-yyyy hh:mm a"),
  },
  {
    header: "Suborder ID",
    accessorKey: "suborder_id",
    cell: ({ row }) => row.original.suborder_id ?? "--",
  },
  {
    header: "Patient",
    accessorKey: "patient_name",
    cell: ({ row }) => row.original.patient_name ?? "--",
  },
  {
    header: "Doctor",
    accessorKey: "doctor_name",
    cell: ({ row }) => row.original.doctor_name ?? "--",
  },
  {
    header: "Doctor NPI",
    accessorKey: "doctor_npi",
    cell: ({ row }) => row.original.doctor_npi ?? "--",
  },
  {
    header: "Rx #",
    accessorKey: "rx_number",
    cell: ({ row }) => row.original.rx_number ?? "--",
  },
  {
    header: "Prescribed At",
    accessorKey: "prescribed_at",
    cell: ({ row }) =>
      row.original.prescribed_at
        ? format(row.original.prescribed_at, "MM-dd-yyyy")
        : "--",
  },
  {
    header: "Note",
    accessorKey: "note",
    cell: ({ row }) => (
      <span className="text-sm line-clamp-1">{row.original.note ?? "--"}</span>
    ),
  },
];

function InventoryBatchLogLookup() {
  const onlyEntity = useOnlyEntity();
  const [practiceSearch, setPracticeSearch] = useState<string | undefined>();

  const [queryStates, setQueryStates] = useQueryStates({
    ...dataTableSearchParsers,
    log_types: parseAsArrayOf(
      parseAsStringEnum(Object.values(InventoryBatchLogTypeEnum)),
    ),
    product_types: parseAsArrayOf(
      parseAsStringEnum(Object.values(ProductTypeEnum)),
    ),
  });

  const practiceIds = onlyEntity ? [onlyEntity] : queryStates.entity_ids;

  const practices = useGetEntitiesInfinite(
    {
      search: practiceSearch,
      include_ids: practiceIds,
      services: ["inventory_360"],
    },
    {
      query: {
        placeholderData: keepPreviousData,
        getNextPageParam: (lastPage, allPages) => {
          if (lastPage.items.length === 0) {
            return undefined;
          }
          return allPages.length + 1;
        },
      },
    },
  );

  const logs = useGetInventoryBatchLogs(
    {
      page: queryStates.page,
      limit: queryStates.limit,
      search: queryStates.search,
      sort_column: queryStates.sort_column,
      ascending: queryStates.ascending,
      entity_ids: practiceIds,
      log_types: queryStates.log_types,
      created_at_start: queryStates.created_at_start,
      created_at_end: queryStates.created_at_end,
      product_types: queryStates.product_types,
    },
    {
      query: {
        placeholderData: keepPreviousData,
      },
    },
  );

  const tableData: LogTableProps[] | undefined = logs.data?.items.map((log) => {
    return {
      link: log.inventory_batch_id
        ? log.lot_number
          ? linkOptions({
              to: "/inventory/$inventoryId/lot/$lotNumber",
              params: {
                inventoryId: String(log.inventory_id),
                lotNumber: log.lot_number,
              },
            })
          : linkOptions({
              to: "/inventory/$inventoryId/lot/unassigned",
              params: { inventoryId: String(log.inventory_id) },
            })
        : log.suborder_product
          ? linkOptions({
              to: "/suborder/$suborderId",
              params: {
                suborderId: String(log.suborder_product.suborder.suborder_id),
              },
            })
          : undefined,
      inventory_batch_id: log.inventory_batch_id ?? undefined,
      product_name: log.product_name,
      log_type: log.log_type,
      quantity: log.quantity,
      created_at: log.created_at,
      suborder_id: log.suborder_product_id
        ? `${log.suborder_product?.suborder.order_id}-${log.suborder_product?.suborder.suborder_id}`
        : undefined,
      patient_name: log.patient_name ?? null,
      doctor_name: log.doctor_name ?? null,
      doctor_npi: log.doctor_npi ?? null,
      rx_number: log.rx_number ?? null,
      prescribed_at: log.prescribed_at ?? null,
      note: log.note ?? null,
    };
  });

  const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  return (
    <div>
      <title>Inventory Transactions - Legrande Health Portal</title>
      <Header
        crumbs={[
          {
            name: "Transactions",
            link: { to: "/inventory-batch-log/lookup" },
          },
        ]}
      />
      <Card>
        <CardHeader>
          <CardTitle>Inventory Transactions</CardTitle>
          <CardDescription>
            View all inventory transactions. Click on a row to see the inventory
            batch details.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <ParamDataTableToolbar>
            <ParamSearchFilter isFetching={logs.isFetching} />
            {!onlyEntity && (
              <DropdownFilter
                title="Practice"
                options={[
                  {
                    items:
                      practices.data?.pages
                        .flatMap((page) => page.items)
                        .map((practice) => ({
                          label: practice.name,
                          value: practice.entity_id,
                        })) ?? [],
                  },
                ]}
                values={queryStates.entity_ids}
                onChange={(values) => setQueryStates({ entity_ids: values })}
                search={practiceSearch}
                setSearch={setPracticeSearch}
                isFetching={practices.isFetchingNextPage}
                fetchNextPage={practices.fetchNextPage}
              />
            )}
            <DropdownFilter
              title="Type"
              options={[
                {
                  items: Object.values(InventoryBatchLogTypeEnum).map(
                    (type) => ({
                      label: type,
                      value: type,
                    }),
                  ),
                },
              ]}
              values={queryStates.log_types || []}
              onChange={(values) => setQueryStates({ log_types: values })}
            />
            <DropdownFilter
              title="Product Type"
              options={[
                {
                  items: Object.values(ProductTypeEnum).map((type) => ({
                    label: type,
                    value: type,
                  })),
                },
              ]}
              values={queryStates.product_types || []}
              onChange={(values) => setQueryStates({ product_types: values })}
            />
            <ParamDateFilter title="Date" name="created_at" />
            <ParamClearButton />
          </ParamDataTableToolbar>
          <ParamDataTable
            columns={columns}
            data={tableData ?? []}
            isLoading={logs.isLoading}
            isError={logs.isError}
            count={logs.data?.count}
            footerActions={
              <ExportCSV
                onExport={() =>
                  exportInventoryBatchLogs({
                    search: queryStates.search,
                    sort_column: queryStates.sort_column,
                    ascending: queryStates.ascending,
                    entity_ids: practiceIds,
                    log_types: queryStates.log_types,
                    created_at_start: queryStates.created_at_start,
                    created_at_end: queryStates.created_at_end,
                    hidden_columns: queryStates.hidden,
                    timezone: userTimezone,
                    product_types: queryStates.product_types,
                  })
                }
                filename="inventory-transactions"
                requiresPHIWarning={false}
              />
            }
          />
        </CardContent>
      </Card>
    </div>
  );
}
